import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

import { ProfessorTestAssignmentService } from '../../../../core/services/assignments/professor-test-assignment.service';
import {
  TestAssignment,
  TestAssignmentStatus,
  TestAssignmentTarget,
  CreateTestAssignmentDto,
} from '../../../../core/interfaces/test-assignment';
import { SwalService } from '../../../../core/services/utils/swal.service';
import { TestService } from '../../../../core/services/test.service';
import { AuthService } from '../../../../core/services/utils/auth.service';
import { Test } from '../../../../core/interfaces/test';
import { ErrorStateComponent } from '../../../ui/error-state/error-state.component';

import { LocalizedDatePipe } from '../../../../shared/pipes/localized-date.pipe';
type StatusTab = 'all' | TestAssignmentStatus;

interface StatusVisual {
  label: string;
  color: string;
  bg: string;
  icon: string;
}

const STATUS_VISUALS: Record<TestAssignmentStatus, StatusVisual> = {
  draft:  { label: 'ASSIGNMENTS.STATUS.DRAFT',  color: 'var(--cdark5)',   bg: 'rgba(85,85,109,.10)',   icon: 'fa-solid fa-pen-ruler' },
  active: { label: 'ASSIGNMENTS.STATUS.ACTIVE', color: 'var(--cgreen4)',  bg: 'rgba(45,202,114,.12)',  icon: 'fa-solid fa-circle-play' },
  closed: { label: 'ASSIGNMENTS.STATUS.CLOSED', color: 'var(--cred4)',    bg: 'rgba(243,22,78,.10)',   icon: 'fa-solid fa-circle-stop' },
};

@Component({
  selector: 'app-teacher-assignments',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule, ErrorStateComponent, LocalizedDatePipe],
  templateUrl: './assignments.component.html',
  styleUrl: './assignments.component.css',
})
export class TeacherAssignmentsComponent implements OnInit {
  private readonly service = inject(ProfessorTestAssignmentService);
  private readonly testService = inject(TestService);
  private readonly authService = inject(AuthService);
  private readonly swal = inject(SwalService);

  readonly assignments = signal<TestAssignment[]>([]);
  readonly loading = signal<boolean>(false);
  readonly errored = signal<boolean>(false);

  readonly activeTab = signal<StatusTab>('all');
  readonly query = signal<string>('');

  // ── Create wizard ────────────────────────────────────────────
  readonly createOpen = signal(false);
  readonly createStep = signal<1 | 2>(1);
  readonly createSaving = signal(false);
  readonly createError = signal('');

  // Step 1 — test picker
  readonly availableTests = signal<Test[]>([]);
  readonly testsLoading = signal(false);
  readonly testsErrored = signal(false);
  readonly testModeFilter = signal<'all' | 'exam' | 'practice' | 'evaluation'>('all');
  readonly testQuery = signal('');
  readonly selectedTest = signal<Test | null>(null);

  readonly filteredTests = computed(() => {
    const mode = this.testModeFilter();
    const q = this.testQuery().trim().toLowerCase();
    return this.availableTests()
      .filter((t) => mode === 'all' || t.testMode?.toLowerCase() === mode)
      .filter((t) => !q || t.title?.toLowerCase().includes(q) || t.subjectName?.toLowerCase().includes(q));
  });

  // Step 2 — target config
  createForm: CreateTestAssignmentDto = this.emptyForm();

  // ── Edit modal ──────────────────────────────────────────────
  readonly editOpen = signal(false);
  readonly editSaving = signal(false);
  readonly editError = signal('');
  editForm: CreateTestAssignmentDto = this.emptyForm();
  editTargetId = signal<number | null>(null);

  readonly counts = computed(() => {
    const all = this.assignments();
    return {
      all: all.length,
      draft: all.filter((a) => a.status === 'draft').length,
      active: all.filter((a) => a.status === 'active').length,
      closed: all.filter((a) => a.status === 'closed').length,
    };
  });

  readonly filtered = computed(() => {
    const tab = this.activeTab();
    const q = this.query().trim().toLowerCase();
    return this.assignments()
      .filter((a) => tab === 'all' || a.status === tab)
      .filter((a) => {
        if (!q) return true;
        return [
          a.testTitle,
          a.subjectName,
          a.groupName,
          a.studentFullName,
          a.instructions,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(q);
      });
  });

  ngOnInit(): void {
    this.load();
  }

  setTab(tab: StatusTab): void {
    this.activeTab.set(tab);
  }

  onQueryInput(value: string): void {
    this.query.set(value);
  }

  statusVisual(status: string | null | undefined): StatusVisual {
    const key = (status as TestAssignmentStatus) ?? 'draft';
    return STATUS_VISUALS[key] ?? STATUS_VISUALS.draft;
  }

  targetLabel(a: TestAssignment): string {
    if (a.targetType === 'group') return a.groupName ?? `Group #${a.groupId}`;
    if (a.targetType === 'student') return a.studentFullName ?? `Student #${a.studentId}`;
    if (a.targetType === 'subject') return a.subjectName ?? `Subject #${a.subjectId}`;
    return '—';
  }

  targetIcon(a: TestAssignment): string {
    if (a.targetType === 'group') return 'fa-solid fa-users';
    if (a.targetType === 'student') return 'fa-solid fa-user-graduate';
    if (a.targetType === 'subject') return 'fa-solid fa-book';
    return 'fa-solid fa-bullseye';
  }

  // ── Create wizard ────────────────────────────────────────────

  openCreate(): void {
    this.createForm = this.emptyForm();
    this.createError.set('');
    this.createStep.set(1);
    this.selectedTest.set(null);
    this.testQuery.set('');
    this.testModeFilter.set('all');
    this.createOpen.set(true);
    this.loadAvailableTests();
  }

  closeCreate(): void {
    this.createOpen.set(false);
  }

  selectTest(test: Test): void {
    this.selectedTest.set(test);
    this.createForm.testId = test.id;
    this.createStep.set(2);
  }

  backToStep1(): void {
    this.createStep.set(1);
    this.selectedTest.set(null);
    this.createForm.testId = 0;
  }

  onTestModeFilter(mode: 'all' | 'exam' | 'practice' | 'evaluation'): void {
    this.testModeFilter.set(mode);
  }

  onTestQueryInput(value: string): void {
    this.testQuery.set(value);
  }

  submitCreate(): void {
    if (!this.createForm.testId || !this.createForm.targetType) {
      this.createError.set('ASSIGNMENTS.CREATE.MISSING_FIELDS');
      return;
    }
    this.createSaving.set(true);
    this.createError.set('');
    this.service.create(this.createForm).subscribe({
      next: (res) => {
        if (res?.data) {
          this.assignments.set([res.data, ...this.assignments()]);
        }
        this.createSaving.set(false);
        this.createOpen.set(false);
      },
      error: (err) => {
        console.error('Create assignment failed:', err);
        this.createError.set('ASSIGNMENTS.CREATE.ERROR');
        this.createSaving.set(false);
      },
    });
  }

  // ── Edit ────────────────────────────────────────────────────

  openEdit(a: TestAssignment): void {
    this.editTargetId.set(a.id);
    this.editForm = {
      testId: a.testId,
      targetType: a.targetType,
      groupId: a.groupId ?? null,
      studentId: a.studentId ?? null,
      subjectId: a.subjectId ?? null,
      instructions: a.instructions ?? null,
      dueDate: a.dueDate ? a.dueDate.substring(0, 16) : null,
    };
    this.editError.set('');
    this.editOpen.set(true);
  }

  closeEdit(): void {
    this.editOpen.set(false);
  }

  submitEdit(): void {
    const id = this.editTargetId();
    if (id === null) return;
    if (!this.editForm.testId || !this.editForm.targetType) {
      this.editError.set('ASSIGNMENTS.CREATE.MISSING_FIELDS');
      return;
    }
    this.editSaving.set(true);
    this.editError.set('');
    this.service.update(id, this.editForm).subscribe({
      next: (res) => {
        if (res?.data) this.replace(res.data);
        this.editSaving.set(false);
        this.editOpen.set(false);
      },
      error: (err) => {
        console.error('Update assignment failed:', err);
        this.editError.set('ASSIGNMENTS.CREATE.ERROR');
        this.editSaving.set(false);
      },
    });
  }

  // ── Lifecycle ────────────────────────────────────────────────

  activate(a: TestAssignment): void {
    if (a.status !== 'draft') return;
    this.service.activate(a.id).subscribe({
      next: (res) => { if (res?.data) this.replace(res.data); },
      error: (err) => {
        console.error('Activate failed:', err);
        this.swal.error('ASSIGNMENTS.ACTIVATE_ERROR');
      },
    });
  }

  close(a: TestAssignment): void {
    if (a.status !== 'active') return;
    this.swal.confirm('ASSIGNMENTS.CONFIRM_CLOSE', () => {
      this.service.close(a.id).subscribe({
        next: (res) => { if (res?.data) this.replace(res.data); },
        error: (err) => {
          console.error('Close failed:', err);
          this.swal.error('ASSIGNMENTS.CLOSE_ERROR');
        },
      });
    });
  }

  remove(a: TestAssignment): void {
    this.swal.confirm('ASSIGNMENTS.CONFIRM_DELETE', () => {
      this.service.delete(a.id).subscribe({
        next: () => {
          this.assignments.set(this.assignments().filter((x) => x.id !== a.id));
        },
        error: (err) => {
          console.error('Delete failed:', err);
          this.swal.error('ASSIGNMENTS.DELETE_ERROR');
        },
      });
    });
  }

  // ── Target type change helper ────────────────────────────────
  onCreateTargetTypeChange(type: TestAssignmentTarget): void {
    this.createForm.targetType = type;
    this.createForm.groupId = null;
    this.createForm.studentId = null;
    this.createForm.subjectId = null;
  }

  onEditTargetTypeChange(type: TestAssignmentTarget): void {
    this.editForm.targetType = type;
    this.editForm.groupId = null;
    this.editForm.studentId = null;
    this.editForm.subjectId = null;
  }

  // ─────────────────────────────────────────────────────────────
  private replace(updated: TestAssignment): void {
    this.assignments.set(
      this.assignments().map((a) => (a.id === updated.id ? updated : a)),
    );
  }

  private emptyForm(): CreateTestAssignmentDto {
    return {
      testId: 0,
      targetType: 'group',
      groupId: null,
      studentId: null,
      subjectId: null,
      instructions: null,
      dueDate: null,
    };
  }

  /** Rechargement manuel de la liste, déclenché par <app-error-state>. */
  reload(): void {
    this.load();
  }

  /** Rechargement manuel du sélecteur de tests, déclenché par <app-error-state>. */
  reloadTests(): void {
    void this.loadAvailableTests();
  }

  private load(): void {
    this.loading.set(true);
    this.errored.set(false);
    this.service.myAssignments().subscribe({
      next: (res) => {
        this.assignments.set(res?.data ?? []);
        this.loading.set(false);
      },
      error: () => {
        this.assignments.set([]);
        this.errored.set(true);
        this.loading.set(false);
      },
    });
  }

  private async loadAvailableTests(): Promise<void> {
    this.testsLoading.set(true);
    this.testsErrored.set(false);
    try {
      const userId = await this.authService.getUserId();
      if (!userId) {
        this.availableTests.set([]);
        this.testsErrored.set(true);
        this.testsLoading.set(false);
        return;
      }
      this.testService.getByTestsProfessorId(userId).subscribe({
        next: (res) => {
          this.availableTests.set(res?.data ?? []);
          this.testsLoading.set(false);
        },
        error: () => {
          this.availableTests.set([]);
          this.testsErrored.set(true);
          this.testsLoading.set(false);
        },
      });
    } catch {
      this.availableTests.set([]);
      this.testsErrored.set(true);
      this.testsLoading.set(false);
    }
  }

  testModeBadge(mode: string | undefined): { label: string; color: string; bg: string; icon: string } {
    const m = (mode ?? '').toLowerCase();
    if (m === 'exam')       return { label: 'ASSIGNMENTS.MODE.EXAM',       color: 'var(--cred4)',    bg: 'rgba(243,22,78,.10)',   icon: 'fa-solid fa-graduation-cap' };
    if (m === 'practice')   return { label: 'ASSIGNMENTS.MODE.PRACTICE',   color: 'var(--cgreen4)',  bg: 'rgba(45,202,114,.12)',  icon: 'fa-solid fa-dumbbell' };
    if (m === 'evaluation') return { label: 'ASSIGNMENTS.MODE.EVALUATION', color: 'var(--cyellow3)', bg: 'rgba(255,193,7,.12)',   icon: 'fa-solid fa-chart-simple' };
    return { label: mode ?? 'Other', color: 'var(--cdark5)', bg: 'rgba(85,85,109,.08)', icon: 'fa-solid fa-file-alt' };
  }
}
